import React, { useEffect, useMemo, useState } from 'react';
import { RotateCcw, Search } from 'lucide-react';
import { DateRangePicker } from '../../../../../components/ui/DateRangePicker';
import { FilterableSelect } from '../../../../../components/ui/FilterableSelect';
import { Button } from '../../../../../components/ui/Button';
import { DELIVERY_STATUSES } from '../../../../../utils/catalogs';
import { getSubstatuses } from '../../../../../features/webhooks/webhooksUtils';
import {
  DISPATCH_STATE_OPTIONS,
  SCOPE_OPTIONS,
} from '../../../../../features/webhookHistory/webhookHistoryConstants';

const ALL = { value: 'all', label: 'Todos' };

const STATUS_OPTIONS = [
  ALL,
  ...DELIVERY_STATUSES.map((status) => ({ value: status.system_name, label: status.user_name })),
];

/**
 * The substatuses that can be picked: those of the chosen status, or every
 * distinct one when no status is chosen. "No aplica" stays in, because a
 * webhook for `pending / not_specified` is a pair of its own.
 */
const substatusOptionsFor = (status) => {
  const source =
    status && status !== 'all'
      ? getSubstatuses(status)
      : DELIVERY_STATUSES.flatMap((item) => item.substatus);
  const seen = new Set();
  const options = [];
  source.forEach((substatus) => {
    if (seen.has(substatus.system_name)) return;
    seen.add(substatus.system_name);
    options.push({ value: substatus.system_name, label: substatus.user_name });
  });
  return [ALL, ...options];
};

/**
 * @param {object} props
 * @param {object} props.filters - `state.webhookHistory.filters`
 * @param {(key: string, value: any) => void} props.onFilterChange
 * @param {(range: {start_date: string, end_date: string}) => void} props.onDateRangeChange
 * @param {() => void} props.onReset
 * @param {Array<{value: number, label: string}>} props.adminOptions
 * @param {Array<{value: number, label: string, email: string}>} props.sellerOptions
 */
export const WebhookHistoryFilters = ({
  filters,
  onFilterChange,
  onDateRangeChange,
  onReset,
  adminOptions,
  sellerOptions,
}) => {
  const [searchInput, setSearchInput] = useState(filters.search || '');

  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchInput !== filters.search) onFilterChange('search', searchInput);
    }, 600);
    return () => clearTimeout(handler);
  }, [searchInput, filters.search, onFilterChange]);

  useEffect(() => {
    // A reset from outside clears the box too.
    if (!filters.search && searchInput) setSearchInput('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search]);

  const substatusOptions = useMemo(() => substatusOptionsFor(filters.status), [filters.status]);

  const admins = useMemo(() => [ALL, ...adminOptions], [adminOptions]);
  const sellers = useMemo(
    () => [
      ALL,
      ...sellerOptions.map((seller) => ({
        value: seller.value,
        label: seller.email ? `${seller.label} · ${seller.email}` : seller.label,
      })),
    ],
    [sellerOptions]
  );

  return (
    <div className="flex flex-col md:flex-row flex-wrap items-center gap-4" data-testid="webhook-history-filters">
      <div className="flex-1 min-w-[260px] flex items-center gap-2 bg-white dark:bg-[#1a1a1a] border border-border rounded-xl px-4 py-2">
        <Search size={16} className="text-text-muted flex-shrink-0" />
        <input
          type="text"
          placeholder="Buscar por orden, referencia o URL..."
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          aria-label="Buscar webhooks"
          className="bg-transparent text-sm w-full outline-none dark:text-white placeholder:text-text-muted"
        />
      </div>

      <FilterableSelect
        label="Ver:"
        value={filters.scope}
        onChange={(value) => onFilterChange('scope', value)}
        options={SCOPE_OPTIONS}
        testId="webhook-history-scope"
      />

      {filters.scope === 'admin' && (
        <FilterableSelect
          label="Operador:"
          value={filters.adminId}
          onChange={(value) => onFilterChange('adminId', value)}
          options={admins}
          placeholder="Elegir operador"
          panelMinWidth={320}
          testId="webhook-history-admin"
        />
      )}

      {filters.scope === 'seller' && (
        <FilterableSelect
          label="Seller:"
          value={filters.sellerId}
          onChange={(value) => onFilterChange('sellerId', value)}
          options={sellers}
          placeholder="Elegir seller"
          panelMinWidth={320}
          testId="webhook-history-seller"
        />
      )}

      <DateRangePicker
        startDate={filters.start_date}
        endDate={filters.end_date}
        onChange={onDateRangeChange}
      />

      <FilterableSelect
        label="Estado:"
        value={filters.status}
        onChange={(value) => onFilterChange('status', value)}
        options={STATUS_OPTIONS}
        testId="webhook-history-status"
      />

      <FilterableSelect
        label="Subestado:"
        value={filters.substatus}
        onChange={(value) => onFilterChange('substatus', value)}
        options={substatusOptions}
        testId="webhook-history-substatus"
      />

      <FilterableSelect
        label="Envío:"
        value={filters.dispatch_state}
        onChange={(value) => onFilterChange('dispatch_state', value)}
        options={DISPATCH_STATE_OPTIONS}
        testId="webhook-history-dispatch-state"
      />

      <Button variant="ghost" size="sm" icon={RotateCcw} onClick={onReset} data-testid="webhook-history-reset">
        Limpiar
      </Button>
    </div>
  );
};
